import { useEffect, useState } from 'react'
import { useMediaQuery } from 'usehooks-ts'
import { t } from 'i18next'

import { Box, Button, ButtonText, Flex, Icon, Modal, Typography } from '@percent/lemonade'
import { useWindowDimensions } from '@percent/workplace-giving/common/hooks'
import { useAnalytics } from '@percent/workplace-giving/common/hooks/useAnalytics/useAnalytics'
import styles from './VolunteeringFiltersModal.module.scss'
import { OpportunityTypeFilter } from './OpportunityTypeFilter/OpportunityTypeFilter'
import { PreferredTimeFilter } from './PreferredTimeFilter/PreferredTimeFilter'
import { LocationFilter } from './LocationFilter/LocationFilter'
import { VolunteeringFiltersModalProps, FilterType } from './VolunteeringFiltersModal.types'
import { SkillsFilter } from './SkillsFilter/SkillsFilter'
import { OpportunityLocationTypeDto, OpportunityType } from '@percent/workplace-giving/api/opportunity/dto'
import { ActivitiesFilter } from './ActivitiesFilter/ActivitiesFilter'
import { CityFilter } from './CityFilter/CityFilter'
import { CampaignFilter } from './CampaignFilter/CampaignFilter'
import { usePostHogFeatureFlag } from '@percent/workplace-giving/common/hooks/usePostHogFeatureFlag/usePostHogFeatureFlag'
import { POSTHOG_FEATURE_FLAGS } from '@percent/workplace-giving/common/hooks/usePostHogFeatureFlag/posthogFeatureFlags'

export function VolunteeringFiltersModal({
  open,
  handleClose,
  query,
  setQuery,
  clearAllActive,
  countries,
  countryCode,
  onClear
}: VolunteeringFiltersModalProps) {
  const { track } = useAnalytics()
  const isDesktop = useMediaQuery('(min-width: 768px)')
  const { width: windowWidth } = useWindowDimensions()
  const cityCampaignFiltersEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.WPG_CITY_CAMPAIGN_FILTERS)

  // Seed the country from the validated `countryCode` (the country actually applied on the search
  // page), not the raw `query.country` param. `query.country` is empty until the user makes an
  // explicit selection, whereas `countryCode` always reflects the effective filter (including the
  // auth/org default on initial load), so the modal mirrors what the page is really filtering by.
  const [selectedFilters, setSelectedFilters] = useState<FilterType>({
    type: query.type ? (query.type as OpportunityType) : undefined,
    locationType: query.locationType ? (query.locationType as OpportunityLocationTypeDto) : undefined,
    country: countryCode,
    city: (query.city?.filter(Boolean) as string[]) || undefined,
    campaignId: query.campaignId || undefined,
    relevantSkills: query.relevantSkills || undefined,
    timeOfDay: query.timeOfDay || undefined,
    activities: query.activities || undefined
  })

  const resetFiltersToValuesFromQuery = () => {
    setSelectedFilters({
      type: query.type ? (query.type as unknown as OpportunityType) : undefined,
      locationType: query.locationType ? (query.locationType as OpportunityLocationTypeDto) : undefined,
      country: countryCode,
      city: (query.city?.filter(Boolean) as string[]) || undefined,
      campaignId: query.campaignId || undefined,
      relevantSkills: query.relevantSkills || undefined,
      timeOfDay: query.timeOfDay || undefined,
      activities: query.activities || undefined
    })
  }

  useEffect(() => {
    resetFiltersToValuesFromQuery()
  }, [query, countryCode])

  const clearFilters = () => {
    const clearedFiltersValues = {
      locationType: undefined,
      type: undefined,
      country: '',
      city: [],
      campaignId: undefined,
      relevantSkills: [],
      timeOfDay: [],
      activities: []
    }
    setSelectedFilters(clearedFiltersValues)
    setQuery(clearedFiltersValues)
    onClear?.()
  }

  const handleCloseWithoutSaving = () => {
    resetFiltersToValuesFromQuery()
    handleClose()
  }

  return (
    <Modal
      borderRadiiSize="xlarge"
      open={open}
      onClose={handleCloseWithoutSaving}
      blur
      isFullscreen={!isDesktop}
      width={isDesktop ? 552 : windowWidth - 48}
      data-testid="volunteeringFiltersModal"
    >
      <Box className={styles.FiltersWrapper}>
        <Box className={styles.FiltersHeader}>
          <Typography className={styles.FiltersTitle}>{t('workplace_giving.common.filters')}</Typography>
          <button
            type="button"
            onClick={handleCloseWithoutSaving}
            className={styles.FiltersCloseBtn}
            data-testid="closeVolunteeringFiltersModal"
          >
            <Icon name="close" size={4} color="gray300" />
          </button>
        </Box>
        <Flex direction="column" gap="0.5rem">
          <OpportunityTypeFilter selectedFilters={selectedFilters} setSelectedFilters={setSelectedFilters} />
          <LocationFilter
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            countries={countries}
          />
          {cityCampaignFiltersEnabled ? (
            <>
              <CityFilter selectedFilters={selectedFilters} setSelectedFilters={setSelectedFilters} />
              <CampaignFilter selectedFilters={selectedFilters} setSelectedFilters={setSelectedFilters} />
            </>
          ) : null}
          <ActivitiesFilter selectedFilters={selectedFilters} setSelectedFilters={setSelectedFilters} />
          <SkillsFilter selectedFilters={selectedFilters} setSelectedFilters={setSelectedFilters} />
          <PreferredTimeFilter selectedFilters={selectedFilters} setSelectedFilters={setSelectedFilters} />
        </Flex>
        <Box className={styles.FiltersButtonsWrapper}>
          {clearAllActive || Object.values(selectedFilters).find(filter => filter !== undefined)?.length ? (
            <Box style={{ marginRight: 'auto' }}>
              <ButtonText type="button" onClick={clearFilters} data-testid="clearFiltersBtn">
                {t('workplace_giving.common.clearAll')}
              </ButtonText>
            </Box>
          ) : null}
          <Box>
            <Button
              type="button"
              variant="secondary"
              size={!isDesktop ? 'small' : 'medium'}
              shape="oval"
              stretch={!isDesktop}
              data-testid="cancelFiltersBtn"
              onClick={handleCloseWithoutSaving}
            >
              {t('workplace_giving.common.cancel')}
            </Button>
            <Button
              type="button"
              size={!isDesktop ? 'small' : 'medium'}
              shape="oval"
              stretch={!isDesktop}
              data-testid="applyFiltersBtn"
              onClick={() => {
                setQuery({
                  ...selectedFilters
                })
                track({
                  event: 'Volunteering Discover Filters',
                  properties: {
                    filtersValue: selectedFilters
                  }
                })
                handleClose()
              }}
            >
              {t('workplace_giving.common.showResults')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}
